/**
 * Size Check Popup
 *
 * Intercepts add-to-cart BEFORE the AJAX call, shows a confirmation popup.
 *
 * Features:
 *  1. Variant picker – lets the customer switch size inside the popup.
 *     Syncs changes back to the original variant-selects element on the page
 *     and waits for the async variant update before allowing confirm.
 *  2. KiwiSizing size chart – opens ON TOP of this popup (z-index boost).
 *     Uses ks.on('modal_open') API.
 *  3. Close behaviours:
 *       "Kyllä, koko on oikein!" / X / overlay → proceeds with add-to-cart
 *       "Katso mittausopas" (data-kiwi-trigger) → opens Kiwi above popup, popup stays open
 */

if (!customElements.get('size-check-popup')) {
  customElements.define('size-check-popup', class SizeCheckPopup extends HTMLElement {

    connectedCallback() {
      this._variantReady = true; // becomes false while variant AJAX is in-flight

      // --- "Katso mittausopas" button: opens Kiwi WITHOUT closing popup ---
      this.addEventListener('click', (event) => {
        const kiwiBtn = event.target.closest('[data-kiwi-trigger]');
        if (!kiwiBtn) return;
        event.preventDefault();
        this._openKiwi();
      });

      // --- Watch for popup open/close via is-open class ---
      let wasOpen = false;

      this._observer = new MutationObserver(() => {
        const isOpen = this.classList.contains('is-open');

        if (!wasOpen && isOpen) {
          // Popup just opened
          if (window._sizeCheckPendingForm) {
            this._injectVariantPicker(window._sizeCheckPendingForm);
          }
          // Hide "Katso mittausopas" if Kiwi's button isn't found on this page
          this._toggleGuideButton();
        }

        if (wasOpen && !isOpen) {
          // Popup just closed → proceed with add-to-cart (if not aborted)
          this._clearVariantPicker();

          if (window._sizeCheckPendingForm) {
            const form = window._sizeCheckPendingForm;
            window._sizeCheckPendingForm = null;
            window._sizeCheckConfirmed = true;

            // Wait for any in-flight variant AJAX to settle before submitting
            this._waitForVariantThenSubmit(form);
          }
        }

        wasOpen = isOpen;
      });

      this._observer.observe(this, { attributes: true, attributeFilter: ['class'] });

      // Boost KiwiSizing modal z-index so it appears above our popup
      this._setupKiwiZIndex();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    // -------------------------------------------------------------------------
    // Variant picker
    // -------------------------------------------------------------------------

    /**
     * Finds the size fieldset on the page for the given form,
     * clones it into the popup, and wires up two-way sync.
     */
    _injectVariantPicker(form) {
      const container = this.querySelector('.size-check-variant-picker');
      if (!container) return;

      // Use getAttribute('id') to reliably get the form's ID string.
      // form.id can return the <input name="id"> element instead of the string
      // due to the browser's "named form controls" quirk (Shopify forms have
      // a hidden <input name="id"> for the variant ID).
      const formId = form.getAttribute('id');
      if (!formId) return;

      // Primary: find variant-selects via the form attribute on its inputs.
      // Variant inputs have form="{product_form_id}" so this works regardless
      // of section ID format.
      let variantSelects =
        document.querySelector(
          `variant-selects input[form="${formId}"], variant-selects select[form="${formId}"]`
        )?.closest('variant-selects');

      // Fallback: find by ID (product-form-{sectionId} → variant-selects-{sectionId})
      if (!variantSelects) {
        const sectionId = formId.replace('product-form-', '');
        variantSelects = document.getElementById(`variant-selects-${sectionId}`);
      }

      if (!variantSelects) {
        console.warn('[SizeCheckPopup] variant-selects not found for form:', formId);
        return;
      }

      // Find the fieldset / select wrapper for the size option
      const sizeNames = (this.dataset.sizeOptionNames || 'koko,size')
        .split(',').map(s => s.trim().toLowerCase());

      const sizeField = this._findSizeField(variantSelects, sizeNames);
      if (!sizeField) {
        console.warn('[SizeCheckPopup] size field not found. variant-selects HTML:', variantSelects.innerHTML.slice(0, 300));
        return;
      }

      // Clone and de-duplicate IDs so the page's own inputs aren't affected
      const clone = sizeField.cloneNode(true);
      clone.querySelectorAll('[id]').forEach(el => { el.id = 'sc-' + el.id; });
      clone.querySelectorAll('[for]').forEach(el => {
        el.setAttribute('for', 'sc-' + el.getAttribute('for'));
      });
      // Remove form associations – we handle sync manually
      clone.querySelectorAll('[form]').forEach(el => el.removeAttribute('form'));

      // Inject into the sub-container so the label element is preserved
      const field = container.querySelector('.size-check-variant-picker__field') || container;
      field.innerHTML = '';
      field.appendChild(clone);
      container.hidden = false;

      // Store reference so we can sync changes
      this._variantSelects = variantSelects;
      this._sizeNames      = sizeNames;

      // Listen for changes in the cloned picker
      container.addEventListener('change', this._onCloneChange.bind(this));
    }

    /** Finds the fieldset or dropdown wrapper matching the size option name */
    _findSizeField(variantSelects, sizeNames) {
      // Button-style (radio inputs): fieldset with data-legend on the legend
      for (const fs of variantSelects.querySelectorAll('fieldset.product-form__input')) {
        const legend = fs.querySelector('[data-legend]');
        if (legend && sizeNames.includes(legend.dataset.legend.toLowerCase().trim())) {
          return fs;
        }
      }

      // Dropdown-style: wrapper div with a <select name="options[Koko]">
      for (const dd of variantSelects.querySelectorAll('.product-form__input--dropdown')) {
        const sel = dd.querySelector('select');
        if (sel) {
          // name is "options[Koko]" – extract just the option name
          const optName = sel.getAttribute('name')
            ?.replace(/^options\[/, '').replace(/\]$/, '')
            .toLowerCase().trim();
          if (optName && sizeNames.includes(optName)) return dd;
        }
      }

      // Fallback: match any fieldset / dropdown whose label text contains a size name
      for (const el of variantSelects.querySelectorAll('.product-form__input')) {
        const label = el.querySelector('legend, label');
        const text  = label?.textContent.toLowerCase().trim() ?? '';
        if (sizeNames.some(n => text.includes(n))) return el;
      }

      return null;
    }

    /** Called when user picks a size in the cloned picker inside the popup */
    _onCloneChange(event) {
      if (!this._variantSelects) return;
      const target = event.target;

      if (target.type === 'radio') {
        // Find the matching original radio and click it
        const original = this._variantSelects.querySelector(
          `input[type="radio"][name="${CSS.escape(target.name)}"][value="${CSS.escape(target.value)}"]`
        );
        if (original && !original.checked) {
          // Mark variant as not ready until the AJAX completes
          this._variantReady = false;
          this._disableConfirm(true);

          // querySelector('[name="id"]') is safe here – we're targeting the hidden
          // input element directly, not using form.id (which has the named-control quirk)
          const hiddenInput = window._sizeCheckPendingForm?.querySelector('input[name="id"][type="hidden"]');
          if (hiddenInput) {
            const onVariantUpdated = () => {
              hiddenInput.removeEventListener('change', onVariantUpdated);
              this._variantReady = true;
              this._disableConfirm(false);
            };
            hiddenInput.addEventListener('change', onVariantUpdated);
          }

          original.click(); // triggers variant-selects change → AJAX
        }
      } else if (target.tagName === 'SELECT') {
        const original = this._variantSelects.querySelector(
          `select[name="${CSS.escape(target.name)}"]`
        );
        if (original && original.value !== target.value) {
          this._variantReady = false;
          this._disableConfirm(true);

          const hiddenInput = window._sizeCheckPendingForm?.querySelector('input[name="id"][type="hidden"]');
          if (hiddenInput) {
            const onVariantUpdated = () => {
              hiddenInput.removeEventListener('change', onVariantUpdated);
              this._variantReady = true;
              this._disableConfirm(false);
            };
            hiddenInput.addEventListener('change', onVariantUpdated);
          }

          original.value = target.value;
          original.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    /** Disables or re-enables the confirm button while variant AJAX is in-flight */
    _disableConfirm(disable) {
      const btn = this.querySelector('[data-confirm]');
      if (!btn) return;
      btn.disabled = disable;
      btn.classList.toggle('loading', disable);
    }

    _clearVariantPicker() {
      const container = this.querySelector('.size-check-variant-picker');
      if (container) {
        // Only wipe the injected field, not the label
        const field = container.querySelector('.size-check-variant-picker__field');
        if (field) field.innerHTML = '';
        container.hidden = true;
      }
      this._variantSelects = null;
      this._variantReady   = true;
      this._disableConfirm(false);
    }

    // -------------------------------------------------------------------------
    // Re-submit after popup close
    // -------------------------------------------------------------------------

    /**
     * Waits up to 2 s for the variant AJAX to finish, then re-submits the form.
     * Falls back immediately if already ready.
     */
    _waitForVariantThenSubmit(form) {
      const submit = () => {
        setTimeout(() => form.requestSubmit(), 300);
      };

      if (this._variantReady) {
        submit();
        return;
      }

      // Poll every 100ms for up to 2s
      let elapsed = 0;
      const poll = setInterval(() => {
        elapsed += 100;
        if (this._variantReady || elapsed >= 2000) {
          clearInterval(poll);
          submit();
        }
      }, 100);
    }

    // -------------------------------------------------------------------------
    // KiwiSizing integration
    // -------------------------------------------------------------------------

    /**
     * Hides the "Katso mittausopas" button when the KiwiSizing element
     * (or fallback URL) is not available on the current page.
     */
    _toggleGuideButton() {
      const btn = this.querySelector('[data-kiwi-trigger]');
      if (!btn) return;

      const kiwiSelector = this.dataset.kiwiSelector;
      const fallbackUrl  = this.dataset.guideUrl;

      const kiwiExists = kiwiSelector && document.querySelector(kiwiSelector);
      const hasUrl     = fallbackUrl && fallbackUrl.trim() !== '';

      btn.hidden = !kiwiExists && !hasUrl;
    }

    /** Opens KiwiSizing by clicking its injected button. Popup stays open. */
    _openKiwi() {
      const selector  = this.dataset.kiwiSelector;
      const fallback  = this.dataset.guideUrl;

      if (selector) {
        const trigger = document.querySelector(selector);
        if (trigger) { trigger.click(); return; }
      }
      if (fallback) window.open(fallback, '_blank', 'noopener');
    }

    /**
     * Registers a ks.on('modal_open') listener so KiwiSizing's modal
     * appears above our popup when our popup is open.
     *
     * IMPORTANT: we only boost the z-index when OUR popup is currently open.
     * If KiwiSizing opens normally (without our popup), we leave its z-index
     * untouched so we don't interfere with its own stacking context.
     *
     * We boost ONLY the content container, never the backdrop –
     * boosting the backdrop to the same z-index as the content would make
     * the backdrop cover the modal and block all interaction.
     */
    _setupKiwiZIndex() {
      const register = () => {
        if (typeof ks === 'undefined' || typeof ks.on !== 'function') {
          // ks not yet available (async load) – retry in 200 ms
          setTimeout(register, 200);
          return;
        }

        ks.on('modal_open', ({ modalID }) => {
          // Only boost when our popup is actually open
          if (!this.classList.contains('is-open')) return;

          // rAF gives KiwiSizing time to render the element into the DOM
          requestAnimationFrame(() => {
            const modal = modalID ? document.getElementById(modalID) : null;
            if (!modal) return;

            // Walk up to the direct child of <body> (Kiwi's React portal root)
            // – that is the CONTENT container, not the backdrop
            let el = modal;
            while (el.parentElement && el.parentElement !== document.body) {
              el = el.parentElement;
            }

            // Boost only if we haven't already, to avoid repeated style writes
            if (el.style.zIndex !== '100000') {
              el.style.setProperty('z-index', '100000', 'important');
            }
          });
        });
      };

      // Try immediately – ks queue is available right away even before the
      // plugin finishes loading; if not, the retry loop above handles it.
      register();
    }
  });
}

// =============================================================================
// Intercept add-to-cart submissions (capture phase = before ProductForm handler)
// =============================================================================

document.addEventListener('submit', (event) => {
  const form = event.target;

  if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

  const productFormEl = form.closest('product-form');
  if (!productFormEl || productFormEl.dataset.hasSizeVariants !== 'true') return;

  // Re-submission after popup close – let it through
  if (window._sizeCheckConfirmed) {
    window._sizeCheckConfirmed = false;
    return;
  }

  // Intercept: prevent ProductForm's AJAX and show popup instead.
  // Store the form element reference – use the element itself, not form.id,
  // to avoid the "named form controls" quirk where form.id returns the
  // <input name="id"> element instead of the ID string.
  event.stopImmediatePropagation();
  event.preventDefault();

  window._sizeCheckPendingForm = form; // store the <form> element

  if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers) {
    Woolman.ModalsAndDrawers.showModalOrDrawer('size-check-popup');
  }
}, true); // capture phase
